import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import apiService from '../services/api';

interface ReferralData {
  _id: string;
  affiliateId: {
    _id: string;
    name: string;
    email: string;
    affiliateCode: string;
  };
  referredByAffiliates?: Array<{
    affiliateId: {
      _id: string;
      name: string;
      email: string;
      affiliateCode: string;
    };
    affiliateCode: string;
    referredAt: string;
  }>;
  userData: {
    name: string;
    email: string;
    walletAddress: string;
    ensName?: string;
  };
  walletDisplay?: string;
  status: 'pending' | 'verified' | 'completed' | 'rejected';
  commission: number;
  createdAt: string;
  verifiedAt?: string;
  completedAt?: string;
}

interface AffiliateData {
  _id: string;
  name: string;
  email: string;
  affiliateCode: string;
}

interface ReferralWorkflowManagerProps {
  onClose?: () => void;
}

const ReferralWorkflowManager: React.FC<ReferralWorkflowManagerProps> = ({ onClose }) => {
  const [referrals, setReferrals] = useState<ReferralData[]>([]);
  const [affiliates, setAffiliates] = useState<AffiliateData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReferrals, setSelectedReferrals] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    status: 'all',
    affiliateId: 'all',
    search: '',
  });
  const [bulkAction, setBulkAction] = useState({
    status: 'pending',
    commission: 0,
  });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const { addNotification } = useNotifications();

  const statusOptions = [
    { value: 'all', label: 'All Statuses', color: 'bg-gray-100 text-gray-600' },
    { value: 'pending', label: 'Pending', color: 'bg-brand-blue-light/20 text-brand-blue-light' },
    { value: 'verified', label: 'Verified', color: 'bg-brand-blue/10 text-brand-blue' },
    { value: 'completed', label: 'Completed', color: 'bg-scarf-green/20 text-scarf-green' },
    { value: 'rejected', label: 'Rejected', color: 'bg-brand-red/20 text-brand-red' },
  ];

  const statusUpdateOptions = [
    { value: 'verified', label: 'Accept (Verified)', color: 'bg-brand-blue/10 text-brand-blue' },
    { value: 'completed', label: 'Complete', color: 'bg-scarf-green/20 text-scarf-green' },
    { value: 'rejected', label: 'Reject', color: 'bg-brand-red/20 text-brand-red' },
  ];

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [referralsResponse, affiliatesResponse] = await Promise.all([
        apiService.getReferrals({
          status: filters.status === 'all' ? undefined : filters.status,
          affiliateId: filters.affiliateId === 'all' ? undefined : filters.affiliateId,
        }),
        apiService.getAffiliates(),
      ]);

      if (referralsResponse.success) {
        setReferrals(referralsResponse.data.referrals || []);
      }

      if (affiliatesResponse.success) {
        setAffiliates(affiliatesResponse.data.affiliates || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load referral data',
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters.status, filters.affiliateId, addNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusUpdate = async (referralId: string, status: string, commission?: number) => {
    try {
      const response = await apiService.updateReferralStatus(referralId, status, commission);

      if (response.success) {
        setReferrals(prev => prev.map(ref =>
          ref._id === referralId
            ? {
                ...ref,
                status: status as 'pending' | 'verified' | 'completed' | 'rejected',
                commission: commission || ref.commission,
                verifiedAt: status === 'verified' ? new Date().toISOString() : ref.verifiedAt,
                completedAt: status === 'completed' ? new Date().toISOString() : ref.completedAt,
              }
            : ref
        ));

        addNotification({
          type: 'success',
          title: 'Status Updated',
          message: `Referral status updated to ${status}`,
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Update Failed',
          message: response.message || 'Failed to update referral status',
        });
      }
    } catch (error) {
      console.error('Error updating referral status:', error);
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update referral status',
      });
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedReferrals.size === 0) {
      addNotification({
        type: 'warning',
        title: 'No Selection',
        message: 'Please select referrals to update',
      });
      return;
    }

    try {
      const response = await apiService.bulkUpdateReferralStatus(
        Array.from(selectedReferrals),
        bulkAction.status,
        bulkAction.commission
      );

      if (response.success) {
        setReferrals(prev => prev.map(ref =>
          selectedReferrals.has(ref._id)
            ? {
                ...ref,
                status: bulkAction.status as 'pending' | 'verified' | 'completed' | 'rejected',
                commission: bulkAction.commission || ref.commission,
                verifiedAt: bulkAction.status === 'verified' ? new Date().toISOString() : ref.verifiedAt,
                completedAt: bulkAction.status === 'completed' ? new Date().toISOString() : ref.completedAt,
              }
            : ref
        ));

        setSelectedReferrals(new Set());
        setShowBulkModal(false);

        addNotification({
          type: 'success',
          title: 'Bulk Update Complete',
          message: `Updated ${selectedReferrals.size} referrals to ${bulkAction.status}`,
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Bulk Update Failed',
          message: response.message || 'Failed to update referrals',
        });
      }
    } catch (error) {
      console.error('Error bulk updating referrals:', error);
      addNotification({
        type: 'error',
        title: 'Bulk Update Failed',
        message: 'Failed to update referrals',
      });
    }
  };

  const handleSelectReferral = (referralId: string) => {
    const newSelected = new Set(selectedReferrals);
    if (newSelected.has(referralId)) {
      newSelected.delete(referralId);
    } else {
      newSelected.add(referralId);
    }
    setSelectedReferrals(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedReferrals.size === filteredReferrals.length) {
      setSelectedReferrals(new Set());
    } else {
      setSelectedReferrals(new Set(filteredReferrals.map(ref => ref._id)));
    }
  };

  const filteredReferrals = referrals.filter(referral => {
    const matchesStatus = filters.status === 'all' || referral.status === filters.status;
    const matchesAffiliate = filters.affiliateId === 'all' || referral.affiliateId._id === filters.affiliateId;
    const matchesSearch = filters.search === '' ||
      referral.userData.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      referral.userData.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      referral.affiliateId.name.toLowerCase().includes(filters.search.toLowerCase());

    return matchesStatus && matchesAffiliate && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.color || 'bg-gray-100 text-gray-600';
  };

  const formatWalletDisplay = (referral: ReferralData) => {
    if (referral.walletDisplay) return referral.walletDisplay;
    const address = referral.userData.walletAddress;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-title font-bold text-deep-navy">Referral Workflow Manager</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
          >
            x
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 space-y-4 border border-ice-teal-light shadow-sm">
        <h3 className="text-lg font-bold text-deep-navy">Filters & Search</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="referral-filter-status" className="block text-sm font-bold text-deep-navy mb-2">Status</label>
            <select
              id="referral-filter-status"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 bg-white border border-ice-teal-light rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="referral-filter-affiliate" className="block text-sm font-bold text-deep-navy mb-2">Affiliate</label>
            <select
              id="referral-filter-affiliate"
              value={filters.affiliateId}
              onChange={(e) => setFilters(prev => ({ ...prev, affiliateId: e.target.value }))}
              className="w-full px-3 py-2 bg-white border border-ice-teal-light rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
            >
              <option value="all">All Affiliates</option>
              {affiliates.map(affiliate => (
                <option key={affiliate._id} value={affiliate._id}>
                  {affiliate.name} ({affiliate.affiliateCode})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="referral-filter-search" className="block text-sm font-bold text-deep-navy mb-2">Search</label>
            <input
              id="referral-filter-search"
              type="text"
              placeholder="Search by name, email, or affiliate..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-3 py-2 bg-white border border-ice-teal-light rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchData}
              className="w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-lg transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedReferrals.size > 0 && (
        <div className="bg-brand-blue/10 border border-brand-blue/20 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <span className="text-brand-blue font-bold">
              {selectedReferrals.size} referral{selectedReferrals.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setShowBulkModal(true)}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-lg transition-colors"
            >
              Bulk Update
            </button>
          </div>
        </div>
      )}

      {/* Referrals Table */}
      <div className="bg-white rounded-2xl overflow-hidden border border-ice-teal-light shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-frost-white">
              <tr>
                <th className="py-3 px-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedReferrals.size === filteredReferrals.length && filteredReferrals.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-ice-teal-light bg-white text-brand-blue focus:ring-brand-blue"
                  />
                </th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">User</th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">Wallet</th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">Affiliate</th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">Status</th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">Commission</th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">Date</th>
                <th className="py-3 px-4 text-left text-deep-navy font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    Loading referrals...
                  </td>
                </tr>
              ) : filteredReferrals.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    No referrals found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredReferrals.map((referral) => (
                  <tr key={referral._id} className="border-b border-gray-100 hover:bg-frost-white">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedReferrals.has(referral._id)}
                        onChange={() => handleSelectReferral(referral._id)}
                        className="rounded border-ice-teal-light bg-white text-brand-blue focus:ring-brand-blue"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="text-deep-navy font-bold">{referral.userData.name}</div>
                        <div className="text-gray-500 text-sm">{referral.userData.email}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-mono text-sm text-gray-800">
                      {formatWalletDisplay(referral)}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="text-deep-navy font-bold">{referral.affiliateId.name}</div>
                        <div className="text-gray-500 text-sm">{referral.affiliateId.affiliateCode}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(referral.status)}`}>
                        {referral.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-800">
                      {referral.commission > 0 ? `${referral.commission} WADDLE` : 'Pending'}
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-sm">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        {referral.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleStatusUpdate(referral._id, 'verified')}
                              className="bg-brand-blue/10 hover:bg-brand-blue/20 text-brand-blue px-3 py-1.5 rounded-md text-xs font-bold transition-colors shadow-sm"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(referral._id, 'rejected')}
                              className="bg-red-50 hover:bg-red-100 text-brand-red px-3 py-1.5 rounded-md text-xs font-bold transition-colors shadow-sm"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {referral.status === 'verified' && (
                          <button
                            onClick={() => handleStatusUpdate(referral._id, 'completed', 100)}
                            className="bg-scarf-green/10 hover:bg-scarf-green/20 text-scarf-green px-3 py-1.5 rounded-md text-xs font-bold transition-colors shadow-sm"
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* A real button, so the backdrop can be reached and dismissed from
              the keyboard rather than only with a pointer. */}
          <button
            type="button"
            aria-label="Close bulk update dialog"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowBulkModal(false)}
          />
          <div className="relative bg-white rounded-3xl p-8 max-w-md w-full border border-ice-teal-light shadow-xl">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-6">Bulk Update Referrals</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="bulk-status" className="block text-sm font-bold text-deep-navy mb-2">New Status</label>
                <select
                  id="bulk-status"
                  value={bulkAction.status}
                  onChange={(e) => setBulkAction(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-ice-teal-light rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
                >
                  {statusUpdateOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="bulk-commission" className="block text-sm font-bold text-deep-navy mb-2">Commission (WADDLE)</label>
                <input
                  id="bulk-commission"
                  type="number"
                  min="0"
                  value={bulkAction.commission}
                  onChange={(e) => setBulkAction(prev => ({ ...prev, commission: Number.parseInt(e.target.value, 10) || 0 }))}
                  className="w-full px-3 py-2 bg-white border border-ice-teal-light rounded-lg text-gray-800 focus:outline-none focus:border-brand-blue"
                />
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => setShowBulkModal(false)}
                className="flex-1 bg-frost-white hover:bg-ice-pale text-gray-800 px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdate}
                className="flex-1 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-4 py-2 rounded-lg transition-colors"
              >
                Update {selectedReferrals.size} Referrals
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralWorkflowManager;
